import React, { useState, useEffect, useRef, useCallback } from "react";
import { Toaster } from "../../../common/Toaster/Toaster";
import { CATEGORIES } from "./supportUtils";
import { DEPARTMENTS, PRIORITIES } from "./supportEnums";

/**
 * Small modal for opening a support ticket on behalf of a client. Mirrors the
 * outside-click / body-lock pattern used by EditNutritionModal.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {(payload: { client_id: string, subject: string, category: string }) => Promise<void>|void} props.onSubmit
 */
export default function CreateTicketModal({ isOpen, onClose, onSubmit }) {
  const [clientId, setClientId] = useState("");
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [department, setDepartment] = useState("");
  const [type, setType] = useState("");
  const [priority, setPriority] = useState("normal");
  const [submitting, setSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("error");
  const modalRef = useRef(null);

  const resetAndClose = useCallback(() => {
    setClientId("");
    setSubject("");
    setCategory(CATEGORIES[0]);
    setDepartment("");
    setType("");
    setPriority("normal");
    setSubmitting(false);
    setToastMessage("");
    onClose();
  }, [onClose]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        resetAndClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, resetAndClose]);

  const flashError = (message) => {
    setToastType("error");
    setToastMessage(message);
    setTimeout(() => setToastMessage(""), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId.trim()) return flashError("Client is required.");
    if (!subject.trim()) return flashError("Subject is required.");

    setSubmitting(true);
    try {
      // Only send what was filled in — omitting `department` lets the backend
      // derive it from `category`, which since v1.9.1 covers every category in
      // our vocabulary including PLAN and COACH. The server's copy of that
      // mapping is the only one that cannot go stale.
      await onSubmit({
        client_id: clientId.trim(),
        subject: subject.trim(),
        category,
        priority,
        ...(department ? { department } : {}),
        ...(type.trim() ? { type: type.trim() } : {}),
      });
      resetAndClose();
    } catch (err) {
      flashError(err?.message || "Could not create ticket.");
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {toastMessage && <Toaster message={toastMessage} type={toastType} />}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
      <div ref={modalRef} className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md z-10">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Create ticket for client</h2>
          <button
            type="button"
            onClick={resetAndClose}
            aria-label="Close"
            className="text-gray-500 hover:text-gray-800"
          >
            &#10006;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
            <input
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Client id or email"
              className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/40"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Short summary of the issue"
              className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/40"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-primary/40"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-primary/40"
              >
                {PRIORITIES.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department
              </label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-primary/40"
              >
                <option value="">Derive from category</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={type}
                onChange={(e) => setType(e.target.value)}
                placeholder="Refund, Login…"
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-primary/40"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={`w-full py-2 px-4 rounded-md text-white font-semibold transition duration-300 ${
              submitting ? "bg-gray-400 cursor-not-allowed" : "bg-orange-primary hover:bg-orange-600"
            }`}
          >
            {submitting ? "Creating…" : "Create ticket"}
          </button>
        </form>
      </div>
    </div>
  );
}
